// The profile gate: sits between RequireAuth and Layout (SaaS only) and holds
// the whole app back until the account carries the contact identity the
// register form normally collects (company/trade name + phone). GitHub OAuth
// signups skip that form; the backend rejects every feature call with
// 403 profile_incomplete until POST /auth/profile succeeds, so this screen is
// the only thing a gated user can meaningfully do. It also offers the exit:
// a user who does not want to provide the info can delete the account and
// leave. Using the platform counts as accepting the Terms of Service and
// Privacy Policy; the gate says so next to the form.
import { useState, type FormEvent, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { meApi, type PlatformUser } from '../api/client'
import { useAuthStore } from '../store/authStore'
import { isSelfHosted } from '../config/edition'
import { resetAnalytics } from '../lib/analytics'
import { Alert, Button, Card, ConfirmDialog, Field, Input, LinkButton, Spinner } from './ui'
import { extractApiError } from '../pages/users/format'

export default function ProfileGate({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const { data: me, isLoading } = useQuery({
    queryKey: ['me'],
    queryFn: meApi.get,
    staleTime: 5 * 60 * 1000,
    enabled: isAuthenticated && !isSelfHosted,
  })

  if (isSelfHosted) return <>{children}</>
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Spinner className="w-6 h-6 text-gray-400" />
      </div>
    )
  }
  // Fail open on a failed /auth/me: the backend middleware still enforces the
  // gate, and a transient error must not lock a paying customer out of the UI.
  if (!me) return <>{children}</>

  const incomplete = me.role !== 'admin' && (!me.company_name || !me.phone)
  if (incomplete) return <CompleteProfileScreen me={me} />
  return <>{children}</>
}

function CompleteProfileScreen({ me }: { me: PlatformUser }) {
  const { t, i18n } = useTranslation()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const logout = useAuthStore((s) => s.logout)

  const [companyName, setCompanyName] = useState(me.company_name ?? '')
  const [phone, setPhone] = useState(me.phone ?? '')
  const [error, setError] = useState('')
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  const tr = i18n.language?.startsWith('tr')
  const termsPath = tr ? '/hizmet-sartlari' : '/terms'
  const privacyPath = tr ? '/gizlilik-politikasi' : '/privacy'

  const saveMutation = useMutation({
    mutationFn: () => meApi.completeProfile(companyName.trim(), phone.trim()),
    onSuccess: () => {
      setError('')
      // The fresh `me` carries the new contact info, which lifts the gate.
      qc.invalidateQueries({ queryKey: ['me'] })
    },
    onError: (e: unknown) => setError(extractApiError(e) ?? t('profileGate.saveError')),
  })

  const deleteMutation = useMutation({
    mutationFn: meApi.deleteSelf,
    onSuccess: () => {
      resetAnalytics()
      logout()
      navigate('/login')
    },
    onError: (e: unknown) => {
      setConfirmingDelete(false)
      setError(extractApiError(e) ?? t('profileGate.deleteError'))
    },
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError('')
    saveMutation.mutate()
  }

  const handleLogout = () => {
    resetAnalytics()
    logout()
    navigate('/login')
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">{t('profileGate.title')}</h1>
          <p className="text-gray-500 text-sm mt-1">{t('profileGate.subtitle')}</p>
        </div>

        <Card>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <Alert tone="danger">{error}</Alert>}

            <Field label={t('profileGate.email')}>
              {(p) => <Input {...p} type="email" value={me.email} disabled />}
            </Field>

            <Field label={t('register.companyName')} hint={t('register.companyNameHint')}>
              {(p) => (
                <Input
                  {...p}
                  type="text"
                  required
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  autoComplete="organization"
                  placeholder={t('register.companyNamePlaceholder')}
                />
              )}
            </Field>

            <Field label={t('register.phone')}>
              {(p) => (
                <Input
                  {...p}
                  type="tel"
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  autoComplete="tel"
                  placeholder={t('register.phonePlaceholder')}
                />
              )}
            </Field>

            <p className="text-xs text-gray-500">
              {t('profileGate.consent')}{' '}
              <Link to={termsPath} target="_blank" className="text-brand-600 hover:text-brand-700 underline">
                {t('profileGate.termsLink')}
              </Link>
              {' '}&bull;{' '}
              <Link to={privacyPath} target="_blank" className="text-brand-600 hover:text-brand-700 underline">
                {t('profileGate.privacyLink')}
              </Link>
            </p>

            <Button type="submit" loading={saveMutation.isPending} fullWidth>
              {saveMutation.isPending ? t('profileGate.saving') : t('profileGate.save')}
            </Button>
          </form>
        </Card>

        <div className="flex items-center justify-between mt-4 px-1">
          <LinkButton tone="neutral" onClick={handleLogout}>
            {t('profileGate.logout')}
          </LinkButton>
          <LinkButton tone="danger" onClick={() => setConfirmingDelete(true)}>
            {t('profileGate.deleteCta')}
          </LinkButton>
        </div>

        <ConfirmDialog
          open={confirmingDelete}
          title={t('profileGate.deleteTitle')}
          message={t('profileGate.deleteMessage')}
          confirmLabel={t('profileGate.deleteConfirm')}
          busy={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate()}
          onCancel={() => setConfirmingDelete(false)}
        />
      </div>
    </div>
  )
}
